import { Color } from "../../../maths/Color";
import { Matrix3x3 } from "../../../maths/Matrix3x3";
import { Matrix4x4 } from "../../../maths/Matrix4x4";
import { Vector2 } from "../../../maths/Vector2";
import { Vector3 } from "../../../maths/Vector3";
import { Vector4 } from "../../../maths/Vector4";
import { Resource } from "../../../resource/Resource";
import { WebGLEngine } from "../../WebGLDriver/RenderDevice/WebGLEngine";
import { ShaderData } from "../../DriverDesign/RenderDevice/ShaderData";
import { WebDefineDatas } from "./WebDefineDatas";
import { WebGLUniformBuffer } from "../../WebGLDriver/RenderDevice/WebGLUniformBuffer";
import { WebGLSubUniformBuffer } from "../../WebGLDriver/RenderDevice/WebGLSubUniformBuffer";
import { WebGLUniformBufferBase } from "../../WebGLDriver/RenderDevice/WebGLUniformBufferBase";
import { Shader3D } from "../../../RenderEngine/RenderShader/Shader3D";
import { Config } from "../../../../Config";
export class WebGLShaderData extends ShaderData {
    constructor(ownerResource = null) {
        super(ownerResource);
        this._data = null;
        this._defineDatas = new WebDefineDatas();
        this._needCacheData = false;
        this._updateCacheArray = null;
        this._subUboBufferNumber = 0;
        this._initData();
    }
    _initData() {
        this._data = {};
        this._updateCacheArray = {};
        this._gammaColorMap = new Map();
        this._uniformBuffers = new Map();
        this._subUniformBuffers = new Map();
        this._uniformBuffersPropertyMap = new Map();
    }
    createUniformBuffer(name, uniformMap) {
        if (!Config._uniformBlock || this._uniformBuffers.has(name)) {
            return null;
        }
        this._needCacheData = true;
        let uboBuffer = new WebGLUniformBuffer(name);
        uniformMap.forEach(uniform => {
            uboBuffer.addUniform(uniform.id, uniform.uniformtype, uniform.arrayLength);
        });
        uboBuffer.create();
        this._uniformBuffers.set(name, uboBuffer);
        let id = Shader3D.propertyNameToID(name);
        this._data[id] = uboBuffer;
        uniformMap.forEach(uniform => {
            let uniformId = uniform.id;
            let data = this._data[uniformId];
            if (data != null) {
                uboBuffer.setUniformData(uniformId, uniform.uniformtype, data);
            }
            this._uniformBuffersPropertyMap.set(uniformId, uboBuffer);
        });
        return uboBuffer;
    }
    updateUBOBuffer(name) {
        if (!Config._uniformBlock) {
            return;
        }
        let buffer = this._uniformBuffers.get(name) || this._subUniformBuffers.get(name);
        if (!buffer) {
            return;
        }
        for (var i in this._updateCacheArray) {
            let index = parseInt(i);
            let ubo = this._uniformBuffersPropertyMap.get(index);
            if (ubo) {
                this._updateCacheArray[i].call(ubo, index, this._data[index]);
            }
        }
        this._updateCacheArray = {};
        buffer.needUpload && buffer.upload();
    }
    createSubUniformBuffer(name, cacheName, uniformMap) {
        let subBuffer = this._subUniformBuffers.get(cacheName);
        if (subBuffer) {
            if (this._subUboBufferNumber < 2) {
                for (var i in this._updateCacheArray) {
                    let index = parseInt(i);
                    let ubo = this._uniformBuffersPropertyMap.get(index);
                    if (ubo) {
                        this._updateCacheArray[i].call(ubo, index, this._data[index]);
                    }
                }
                this._updateCacheArray = {};
            }
            else {
                uniformMap.forEach((uniform, index) => {
                    if (this._data[index] && this._updateCacheArray[index]) {
                        this._updateCacheArray[index].call(subBuffer, index, this._data[index]);
                    }
                });
            }
            return subBuffer;
        }
        let engine = WebGLEngine.instance;
        let mgr = engine.bufferMgr;
        let uniformBuffer = new WebGLSubUniformBuffer(name, uniformMap, mgr, this);
        this._subUboBufferNumber++;
        this._needCacheData = true;
        uniformBuffer.notifyGPUBufferChange();
        this._subUniformBuffers.set(cacheName, uniformBuffer);
        let id = Shader3D.propertyNameToID(name);
        this._data[id] = uniformBuffer;
        uniformMap.forEach(uniform => {
            let uniformId = uniform.id;
            let data = this._data[uniformId];
            if (data != null) {
                uniformBuffer.setUniformData(uniformId, uniform.uniformtype, data);
            }
            this._uniformBuffersPropertyMap.set(uniformId, uniformBuffer);
        });
        return uniformBuffer;
    }
    getData() {
        return this._data;
    }
    addDefine(define) {
        this._defineDatas.add(define);
    }
    addDefines(define) {
        this._defineDatas.addDefineDatas(define);
    }
    removeDefine(define) {
        this._defineDatas.remove(define);
    }
    removeDefines(defines) {
        this._defineDatas.removeDefineDatas(defines);
    }
    hasDefine(define) {
        return this._defineDatas.has(define);
    }
    clearDefine() {
        this._defineDatas.clear();
    }
    clearData() {
        for (const key in this._data) {
            if (this._data[key] instanceof Resource) {
                this._data[key]._removeReference();
            }
        }
        this._uniformBuffersPropertyMap.clear();
        this._uniformBuffers.forEach(buffer => {
            buffer.destroy();
        });
        this._uniformBuffers.clear();
        this._subUniformBuffers.forEach(buffer => {
            buffer.destroy();
        });
        this._subUniformBuffers.clear();
        this._data = {};
        this._gammaColorMap.clear();
        this.clearDefine();
        this._needCacheData = false;
        this._subUboBufferNumber = 0;
    }
    getBool(index) {
        return this._data[index];
    }
    setBool(index, value) {
        this._data[index] = value;
        if (this._needCacheData) {
        }
    }
    getInt(index) {
        return this._data[index];
    }
    setInt(index, value) {
        this._data[index] = value;
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setInt;
        }
    }
    getNumber(index) {
        return this._data[index];
    }
    setNumber(index, value) {
        this._data[index] = value;
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setFloat;
        }
    }
    getVector2(index) {
        return this._data[index];
    }
    setVector2(index, value) {
        if (this._data[index]) {
            value.cloneTo(this._data[index]);
        }
        else
            this._data[index] = value.clone();
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setVector2;
        }
    }
    getVector3(index) {
        return this._data[index];
    }
    setVector3(index, value) {
        if (this._data[index]) {
            value.cloneTo(this._data[index]);
        }
        else
            this._data[index] = value.clone();
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setVector3;
        }
    }
    getVector(index) {
        return this._data[index];
    }
    setVector(index, value) {
        if (this._data[index]) {
            value.cloneTo(this._data[index]);
        }
        else
            this._data[index] = value.clone();
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setVector4;
        }
    }
    getColor(index) {
        return this._gammaColorMap.get(index);
    }
    setColor(index, value) {
        if (!value)
            return;
        if (this._data[index]) {
            let gammaColor = this._gammaColorMap.get(index);
            value.cloneTo(gammaColor);
            let linearColor = this._data[index];
            linearColor.x = Color.gammaToLinearSpace(value.r);
            linearColor.y = Color.gammaToLinearSpace(value.g);
            linearColor.z = Color.gammaToLinearSpace(value.b);
            linearColor.w = value.a;
        }
        else {
            let linearColor = new Vector4();
            linearColor.x = Color.gammaToLinearSpace(value.r);
            linearColor.y = Color.gammaToLinearSpace(value.g);
            linearColor.z = Color.gammaToLinearSpace(value.b);
            linearColor.w = value.a;
            this._data[index] = linearColor;
            this._gammaColorMap.set(index, value.clone());
        }
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setVector4;
        }
    }
    getLinearColor(index) {
        return this._data[index];
    }
    getMatrix4x4(index) {
        return this._data[index];
    }
    setMatrix4x4(index, value) {
        if (this._data[index]) {
            value.cloneTo(this._data[index]);
        }
        else {
            this._data[index] = value.clone();
        }
        if (this._needCacheData)
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setMatrix4x4;
    }
    getMatrix3x3(index) {
        return this._data[index];
    }
    setMatrix3x3(index, value) {
        if (this._data[index]) {
            value.cloneTo(this._data[index]);
        }
        else {
            this._data[index] = value.clone();
        }
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setMatrix3x3;
        }
    }
    getBuffer(index) {
        return this._data[index];
    }
    setBuffer(index, value) {
        this._data[index] = value;
        if (this._needCacheData) {
            this._updateCacheArray[index] = WebGLUniformBufferBase.prototype.setArrayBuffer;
        }
    }
    setTexture(index, value) {
        var lastValue = this._data[index];
        if (value) {
            let shaderDefine = WebGLEngine._texGammaDefine[index];
            if (shaderDefine && value && value.gammaCorrection > 1) {
                this.addDefine(shaderDefine);
            }
            else {
                shaderDefine && this.removeDefine(shaderDefine);
            }
        }
        this._data[index] = value;
        lastValue && lastValue._removeReference();
        value && value._addReference();
    }
    _setInternalTexture(index, value) {
        var lastValue = this._data[index];
        if (value) {
            let shaderDefine = WebGLEngine._texGammaDefine[index];
            if (shaderDefine && value && value.gammaCorrection > 1) {
                this.addDefine(shaderDefine);
            }
            else {
                shaderDefine && this.removeDefine(shaderDefine);
            }
        }
        this._data[index] = value;
    }
    getTexture(index) {
        return this._data[index];
    }
    getSourceIndex(value) {
        for (var i in this._data) {
            if (this._data[i] == value)
                return Number(i);
        }
        return -1;
    }
    cloneTo(destObject) {
        destObject.clearData();
        var destData = destObject._data;
        for (var k in this._data) {
            var value = this._data[k];
            if (value != null) {
                if (typeof value == "number") {
                    destData[k] = value;
                }
                else if (typeof value == "boolean") {
                    destData[k] = value;
                }
                else if (value instanceof Vector2) {
                    let v2 = destData[k] || (destData[k] = new Vector2());
                    value.cloneTo(v2);
                }
                else if (value instanceof Vector3) {
                    let v3 = destData[k] || (destData[k] = new Vector3());
                    value.cloneTo(v3);
                }
                else if (value instanceof Vector4) {
                    let color = this.getColor(parseInt(k));
                    if (color) {
                        let clonecolor = color.clone();
                        destObject.setColor(parseInt(k), clonecolor);
                    }
                    else {
                        let v4 = destData[k] || (destData[k] = new Vector4());
                        value.cloneTo(v4);
                    }
                }
                else if (value instanceof Matrix3x3) {
                    let mat = destData[k] || (destData[k] = new Matrix3x3());
                    value.cloneTo(mat);
                }
                else if (value instanceof Matrix4x4) {
                    let mat = destData[k] || (destData[k] = new Matrix4x4());
                    value.cloneTo(mat);
                }
                else if (value instanceof Resource) {
                    destData[k] = value;
                    value._addReference();
                }
            }
        }
        this._defineDatas.cloneTo(destObject._defineDatas);
        this._gammaColorMap.forEach((color, index) => {
            destObject._gammaColorMap.set(index, color.clone());
        });
    }
    getDefineData() {
        return this._defineDatas;
    }
    clone() {
        var dest = new WebGLShaderData();
        this.cloneTo(dest);
        return dest;
    }
    destroy() {
        this.clearData();
        this._defineDatas.destroy();
        this._defineDatas = null;
        this._gammaColorMap.clear();
        this._gammaColorMap = null;
    }
}
